// AssoDons.jsx — Bureau : enregistrer des dons + générer les reçus fiscaux CERFA.
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { usePermissions } from '@/lib/usePermissions';
import { downloadAuthed } from '@/lib/media';
import { toast } from 'sonner';
import {
  Loader2, Receipt, Plus, Trash2, Download, AlertTriangle, Save, ChevronDown, Building2,
} from 'lucide-react';

const ORG_TYPES = [
  { value: 'interet_general', label: 'Intérêt général (art. 200 du CGI)' },
  { value: 'aide_personnes', label: 'Aide aux personnes en difficulté (loi Coluche)' },
  { value: 'mecenat', label: "Mécénat d'entreprise (art. 238 bis)" },
];
const MODES = [
  { value: 'cheque', label: 'Chèque' }, { value: 'virement', label: 'Virement' },
  { value: 'carte', label: 'Carte bancaire' }, { value: 'numeraire', label: 'Espèces' },
  { value: 'prelevement', label: 'Prélèvement' }, { value: 'autre', label: 'Autre' },
];
const NATURES = [
  { value: 'numeraire', label: 'Numéraire' }, { value: 'titres', label: 'Titres' }, { value: 'autre', label: 'Autre' },
];

const emptyDon = () => ({
  donateur: { civilite: 'M.', prenom: '', nom: '', adresse: '', code_postal: '', ville: '', email: '' },
  montant: '', date_don: new Date().toISOString().slice(0, 10),
  mode_paiement: 'cheque', nature: 'numeraire', creer_ecriture_compta: true,
});

const fmtDate = (s) => { try { return new Date(s).toLocaleDateString('fr-FR'); } catch { return s; } };

export default function AssoDons() {
  const { can } = usePermissions();
  const canWrite = can('compta', 'write');
  const canConfig = can('config', 'update');

  const [data, setData] = useState({ dons: [], total_annee: 0, annee: '', count: 0 });
  const [fiscal, setFiscal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFiscal, setShowFiscal] = useState(false);
  const [savingFiscal, setSavingFiscal] = useState(false);
  const [form, setForm] = useState(emptyDon());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [dR, fR] = await Promise.all([api.get('/dons'), api.get('/assos/fiscal')]);
      setData(dR.data); setFiscal(fR.data);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveFiscal = async () => {
    setSavingFiscal(true);
    try { const { data: f } = await api.put('/assos/fiscal', fiscal); setFiscal(f); toast.success('Infos fiscales enregistrées'); }
    catch { toast.error('Enregistrement impossible'); }
    finally { setSavingFiscal(false); }
  };

  const createDon = async (e) => {
    e.preventDefault();
    if (!form.montant || Number(form.montant) <= 0) return toast.error('Montant invalide');
    if (!form.donateur.nom) return toast.error('Nom du donateur requis');
    setSaving(true);
    try {
      await api.post('/dons', { ...form, montant: Number(form.montant) });
      toast.success('Don enregistré · reçu fiscal généré');
      setForm(emptyDon()); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer ce don et son reçu fiscal ?')) return;
    try { await api.delete(`/dons/${id}`); toast.success('Don supprimé'); load(); }
    catch { toast.error('Suppression impossible'); }
  };

  const download = (d) =>
    downloadAuthed(`/api/dons/${d.id}/recu`, `recu-fiscal-${d.numero_recu}.pdf`).catch(() => toast.error('Téléchargement impossible'));

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updDon = (k, v) => setForm((f) => ({ ...f, donateur: { ...f.donateur, [k]: v } }));
  const updF = (k, v) => setFiscal((x) => ({ ...x, [k]: v }));

  if (loading) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="dons-page">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black text-slate-900 flex items-center gap-2">
            <Receipt className="w-6 h-6" /> Dons &amp; reçus fiscaux
          </h1>
          <p className="text-sm text-slate-500 mt-1">Enregistrez les dons et générez les reçus CERFA (art. 200 / 238 bis du CGI).</p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xs uppercase tracking-wide text-slate-400">Collecté {data.annee}</div>
          <div className="font-display text-2xl font-black" style={{ color: 'var(--theme-primary)' }} data-testid="dons-total-annee">{(data.total_annee || 0).toFixed(2)} €</div>
        </div>
      </header>

      {!fiscal?.configured && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="dons-fiscal-warning">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <b>Complétez les infos fiscales</b> de l&apos;association pour que les reçus soient valables (adresse, type d&apos;organisme, signataire).
            {canConfig && <button onClick={() => setShowFiscal(true)} className="ml-1 underline font-semibold">Configurer maintenant</button>}
          </div>
        </div>
      )}

      {/* Infos fiscales */}
      {canConfig && (
        <div className="card" data-testid="dons-fiscal-card">
          <button onClick={() => setShowFiscal((s) => !s)} className="w-full flex items-center justify-between" data-testid="dons-fiscal-toggle">
            <span className="font-semibold text-slate-900 flex items-center gap-2"><Building2 className="w-4 h-4" /> Infos fiscales de l&apos;association</span>
            <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${showFiscal ? 'rotate-180' : ''}`} />
          </button>
          {showFiscal && fiscal && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FieldInput label="Raison sociale" value={fiscal.raison_sociale} onChange={(v) => updF('raison_sociale', v)} testid="fiscal-raison" />
              <div>
                <label className="label-uppercase block mb-1.5">Type d&apos;organisme</label>
                <select value={fiscal.type_organisme} onChange={(e) => updF('type_organisme', e.target.value)} className="input-base" data-testid="fiscal-type">
                  {ORG_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <FieldInput label="Adresse" value={fiscal.adresse} onChange={(v) => updF('adresse', v)} testid="fiscal-adresse" className="sm:col-span-2" />
              <FieldInput label="Code postal" value={fiscal.code_postal} onChange={(v) => updF('code_postal', v)} testid="fiscal-cp" />
              <FieldInput label="Ville" value={fiscal.ville} onChange={(v) => updF('ville', v)} testid="fiscal-ville" />
              <FieldInput label="N° RNA" value={fiscal.numero_rna} onChange={(v) => updF('numero_rna', v)} testid="fiscal-rna" />
              <FieldInput label="N° SIRET" value={fiscal.numero_siret} onChange={(v) => updF('numero_siret', v)} testid="fiscal-siret" />
              <div className="sm:col-span-2">
                <label className="label-uppercase block mb-1.5">Objet de l&apos;association</label>
                <textarea rows="2" value={fiscal.objet} onChange={(e) => updF('objet', e.target.value)} maxLength={600} className="input-base" data-testid="fiscal-objet" />
              </div>
              <FieldInput label="Nom du signataire" value={fiscal.signataire_nom} onChange={(v) => updF('signataire_nom', v)} testid="fiscal-sign-nom" />
              <FieldInput label="Qualité du signataire" value={fiscal.signataire_qualite} onChange={(v) => updF('signataire_qualite', v)} testid="fiscal-sign-qualite" />
              <div className="sm:col-span-2 flex justify-end">
                <button onClick={saveFiscal} disabled={savingFiscal} className="btn-primary flex items-center gap-1.5" data-testid="fiscal-save">
                  {savingFiscal ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Enregistrer un don */}
      {canWrite && (
        <form onSubmit={createDon} className="card space-y-3" data-testid="dons-form">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2"><Plus className="w-4 h-4" /> Enregistrer un don</h2>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <label className="label-uppercase block mb-1.5">Civilité</label>
              <select value={form.donateur.civilite} onChange={(e) => updDon('civilite', e.target.value)} className="input-base" data-testid="don-civilite">
                <option value="M.">M.</option><option value="Mme">Mme</option><option value="">—</option>
              </select>
            </div>
            <FieldInput label="Prénom" value={form.donateur.prenom} onChange={(v) => updDon('prenom', v)} testid="don-prenom" />
            <FieldInput label="Nom *" value={form.donateur.nom} onChange={(v) => updDon('nom', v)} testid="don-nom" />
            <FieldInput label="Email (lien adhérent)" value={form.donateur.email} onChange={(v) => updDon('email', v)} testid="don-email" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <FieldInput label="Adresse" value={form.donateur.adresse} onChange={(v) => updDon('adresse', v)} testid="don-adresse" className="sm:col-span-2" />
            <FieldInput label="Code postal" value={form.donateur.code_postal} onChange={(v) => updDon('code_postal', v)} testid="don-cp" />
            <FieldInput label="Ville" value={form.donateur.ville} onChange={(v) => updDon('ville', v)} testid="don-ville" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <label className="label-uppercase block mb-1.5">Montant (€) *</label>
              <input type="number" step="0.01" min="0" value={form.montant} onChange={(e) => upd('montant', e.target.value)} className="input-base" data-testid="don-montant" />
            </div>
            <div>
              <label className="label-uppercase block mb-1.5">Date du don</label>
              <input type="date" value={form.date_don} onChange={(e) => upd('date_don', e.target.value)} className="input-base" data-testid="don-date" />
            </div>
            <div>
              <label className="label-uppercase block mb-1.5">Mode</label>
              <select value={form.mode_paiement} onChange={(e) => upd('mode_paiement', e.target.value)} className="input-base" data-testid="don-mode">
                {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-uppercase block mb-1.5">Nature</label>
              <select value={form.nature} onChange={(e) => upd('nature', e.target.value)} className="input-base" data-testid="don-nature">
                {NATURES.map((n) => <option key={n.value} value={n.value}>{n.label}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={form.creer_ecriture_compta} onChange={(e) => upd('creer_ecriture_compta', e.target.checked)} data-testid="don-compta" />
              Créer l&apos;écriture comptable (recette « Dons »)
            </label>
            <button type="submit" disabled={saving} className="btn-primary flex items-center gap-1.5" data-testid="don-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Enregistrer &amp; générer le reçu
            </button>
          </div>
        </form>
      )}

      {/* Liste des dons */}
      <div className="card" data-testid="dons-list">
        <h2 className="font-semibold text-slate-900 mb-3">Historique des dons ({data.count})</h2>
        {data.dons.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center" data-testid="dons-empty">Aucun don enregistré pour l&apos;instant.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.dons.map((d) => (
              <div key={d.id} className="py-3 flex items-center justify-between gap-3" data-testid={`don-row-${d.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">
                    {(d.donateur?.civilite || '') + ' ' + (d.donateur?.prenom || '') + ' ' + (d.donateur?.nom || '')}
                    <span className="ml-2 text-xs font-mono text-slate-400">n° {d.numero_recu}</span>
                  </div>
                  <div className="text-xs text-slate-500">{fmtDate(d.date_don)} · {MODES.find((m) => m.value === d.mode_paiement)?.label || d.mode_paiement}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-semibold text-slate-900">{(d.montant || 0).toFixed(2)} €</span>
                  <button onClick={() => download(d)} className="btn-secondary flex items-center gap-1.5 !py-1.5 !px-3" data-testid={`don-recu-${d.id}`}><Download className="w-4 h-4" /> Reçu</button>
                  {canWrite && <button onClick={() => del(d.id)} className="text-rose-500 hover:text-rose-600 p-2" data-testid={`don-delete-${d.id}`} aria-label="Supprimer"><Trash2 className="w-4 h-4" /></button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FieldInput({ label, value, onChange, testid, className = '' }) {
  return (
    <div className={className}>
      <label className="label-uppercase block mb-1.5">{label}</label>
      <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} className="input-base" data-testid={testid} />
    </div>
  );
}
